import { useState, useMemo, forwardRef, useEffect, useRef } from "react"
import EyeHideIcon from "components/icons/EyeHideIcon"
import EyeIcon from "components/icons/EyeIcon"
import { useLang } from "hooks/useLang"
import { twMerge } from "tailwind-merge"

interface IProps extends React.InputHTMLAttributes<HTMLInputElement> {
    onChange: React.ChangeEventHandler<HTMLInputElement>
    inputID?: string
    label?: string
    name: string
    value?: string | number | readonly string[] | undefined
    error?: string | React.ReactNode
    reqStar?: boolean
    labelClassName?: string
    disableCapitalize?: boolean
    inputContainerClassName?: string
    type:
    | "text"
    | "email"
    | "password"
    | "date"
    | "radio"
    | "tel"
    | "number"
    | "checkbox"
    | "textarea"
    | "phoneOrEmail"
    showPasswordRequirements?: boolean
}

const InputNew = forwardRef<HTMLDivElement, IProps>(({
    onChange,
    inputID,
    label,
    type,
    name,
    value,
    error,
    reqStar = false,
    labelClassName,
    disableCapitalize = false,
    inputContainerClassName,
    required,
    showPasswordRequirements,
    ...props
}, ref) => {
    const [showPassword, setShowPassword] = useState(false)
    const [isFocused, setIsFocused] = useState(false)
    // Visible error with hysteresis
    const [visibleError, setVisibleError] = useState<string | React.ReactNode | undefined>(error)
    const typingTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null)
    const showTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null)
    const hideTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null)
    const errorRef = useRef<typeof error>(error)
    const [isTyping, setIsTyping] = useState(false)
    const [suppressWhileTyping, setSuppressWhileTyping] = useState(false)
    const { t } = useLang()

    const handleFocus = () => {
        setIsFocused(true)
    }

    const handleBlur = () => {
        setIsFocused(false)
    }

    const validatePassword = (password: string) => {
        const hasMinLength = password.length >= 8
        const hasUpperCase = /[A-Z]/.test(password)
        const hasLowerCase = /[a-z]/.test(password)
        const hasNumber = /[0-9]/.test(password)

        return {
            hasMinLength,
            hasUpperCase,
            hasLowerCase,
            hasNumber,
            isValid: hasMinLength && hasUpperCase && hasLowerCase && hasNumber
        }
    }

    const passwordValidation = type === "password" && value ? validatePassword(value as string) : null

    const displayError = suppressWhileTyping ? undefined : visibleError
    const hasError = Boolean(displayError)

    const style = useMemo(() => {
        return {
            label: `text-sm ${disableCapitalize ? "" : "capitalize"} transition-all md:text-base pb-1 text-gray-600 dark:text-gray-200 ${labelClassName ? labelClassName : ""
                } ${reqStar ? "after:content-['*'] after:text-red-500" : ""}`,
            inputContainer: twMerge(
                `flex border rounded-md transition-all h-12 overflow-hidden inputContainer dark:bg-opacity-20 bg-opacity-40 dark:bg-primary-theme-500`,
                isFocused || value ? "border-[#7B91EF]" : "border-[#F0F0F080]",
                hasError ? "errorStyle" : "",
                type === "password" && value && passwordValidation && !passwordValidation.isValid ? "border-red-500 dark:border-red-400" : "",
                inputContainerClassName
            ),
            passwordEye: `flex w-9 h-full cursor-pointer items-center justify-center hover:bg-gray-100 hover:dark:bg-primary-theme-400 transition-all`,
            requirementText: (isValid: boolean) => 
                `text-xs ${isValid ? 'text-green-500 dark:text-green-400' : hasError ? 'text-red-500 dark:text-red-400' : 'text-gray-500 dark:text-gray-400'}`
        }
    }, [hasError, isFocused, disableCapitalize, labelClassName, reqStar, required, type, value, passwordValidation])

    // Keep a ref of latest error
    useEffect(() => {
        errorRef.current = error
    }, [error])

    // Hysteresis for error visibility: show after 300ms stable error, hide after 800ms stable clear
    useEffect(() => {
        // If typing, hide immediately and cancel timers
        if (suppressWhileTyping) {
            if (showTimerRef.current) clearTimeout(showTimerRef.current)
            if (hideTimerRef.current) clearTimeout(hideTimerRef.current)
            setVisibleError(undefined)
            return
        }

        const hasUpstreamError = Boolean(errorRef.current)

        if (hasUpstreamError) {
            // Cancel hide timer; schedule show after 300ms stable
            if (hideTimerRef.current) {
                clearTimeout(hideTimerRef.current)
                hideTimerRef.current = null
            }
            if (showTimerRef.current) clearTimeout(showTimerRef.current)
            showTimerRef.current = setTimeout(() => {
                setVisibleError(errorRef.current || undefined)
            }, 300)
        } else {
            // No error: cancel show timer and hide immediately
            if (showTimerRef.current) {
                clearTimeout(showTimerRef.current)
                showTimerRef.current = null
            }
            if (hideTimerRef.current) clearTimeout(hideTimerRef.current)
            setVisibleError(undefined)
        }

        return () => {
            // timers are cleaned up on next effect run/unmount as needed
        }
    }, [error, suppressWhileTyping])

    // Wrap onChange to start debounce upon typing
    const handleInternalChange: React.ChangeEventHandler<HTMLInputElement> = (e) => {
        if (typingTimerRef.current) {
            clearTimeout(typingTimerRef.current)
            typingTimerRef.current = null
        }
        setIsTyping(true)
        // Hide error immediately while user is typing
        setSuppressWhileTyping(true)
        onChange(e)
        typingTimerRef.current = setTimeout(() => {
            setIsTyping(false)
            setSuppressWhileTyping(false)
            // No immediate change here; hysteresis effect will handle show/hide
        }, 300)
    }

    useEffect(() => {
        return () => {
            if (typingTimerRef.current) clearTimeout(typingTimerRef.current)
            if (showTimerRef.current) clearTimeout(showTimerRef.current)
            if (hideTimerRef.current) clearTimeout(hideTimerRef.current)
        }
    }, [])

    const setType = () => {
        if (type === "password" && showPassword) {
            return "text"
        } else {
            return type
        }
    }

    if (type === "checkbox") {
        return (
            <input
                onChange={onChange}
                type="checkbox"
                style={{ backgroundColor: "red" }}
                className="h-4 w-4 cursor-pointer"
            />
        )
    }

    return (
        <div className="flex w-full flex-col" ref={ref}>
            <div className="px-2 py-1">
                <label className={style.label} htmlFor={inputID}>
                    {label}
                </label>
            </div>
            <div className={style.inputContainer}>
                <input
                    className="w-full bg-transparent p-2 text-sm placeholder-white-500 placeholder:capitalize placeholder:text-[14px]"
                    value={value}
                    onChange={handleInternalChange}
                    id={inputID}
                    type={setType()}
                    name={name}
                    data-cy={name}
                    onFocus={handleFocus}
                    onBlur={handleBlur}
                    required={required}
                    {...props}
                />
                {type === "password" ? (
                    <div
                        className={style.passwordEye}
                        onClick={() => setShowPassword(!showPassword)}
                    >
                        {!showPassword ? <EyeIcon /> : <EyeHideIcon />}
                    </div>
                ) : null}
            </div>
            <div
                className={twMerge(
                    "transition-all duration-300 ease-out overflow-hidden",
                    hasError ? "opacity-100 max-h-10 mt-1" : "opacity-0 max-h-0 mt-0"
                )}
            >
                {hasError && (
                    <span className="pt-1 text-xs text-red-500 dark:text-red-400 block">
                        {typeof displayError === "string" ? t(displayError) : displayError}
                    </span>
                )}
            </div>
            {type === "password" && showPasswordRequirements && (
                <div className="flex flex-col gap-1 px-2 mt-1">
                    <span className={style.requirementText(passwordValidation?.hasMinLength || false)}>
                        • {t("password_min_length") || "At least 8 characters"}
                    </span>
                    <span className={style.requirementText(passwordValidation?.hasUpperCase || false)}>
                        • {t("password_uppercase") || "One uppercase letter"}
                    </span>
                    <span className={style.requirementText(passwordValidation?.hasLowerCase || false)}>
                        • {t("password_lowercase") || "One lowercase letter"}
                    </span>
                    <span className={style.requirementText(passwordValidation?.hasNumber || false)}>
                        • {t("password_number") || "One number"}
                    </span>
                </div>
            )}
        </div>
    )
})

InputNew.displayName = 'InputNew'

export default InputNew
